/* Kolor marki, from `profile-web-mockups-v2/f-zakladki.html` (`[data-view="marka"]`) and the
 * `.swatches` / `.picker` blocks it borrows from `_blocks.css`.
 *
 * ── The concentric radii are the section's own rule ─────────────────────────────────────────
 * The mockup states it in a comment: „inner radius = outer radius − inset". A 48px tile at radius
 * 16 holds a 32px swatch inset 8px, so the swatch takes 8. Both numbers are tokens rather than a
 * `calc`, because two stylesheets recomputing one derivation is how they come to disagree.
 *
 * ── The picker is HALF the row ──────────────────────────────────────────────────────────────
 * `minmax(0, 1fr) minmax(0, 1fr)`, the mockup's own override of the shared block's `1fr 200px`.
 * Both tracks need `minmax(0, …)`: a grid item's default `min-width: auto` refuses to shrink below
 * its content, and the HEX field inside the right-hand one is wider than half of a 1280px sheet.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* The BOX is `AccountScreen.module.css`'s `.accountButton`. Only the room above it is this
 * block's own. `shared/ui/stateAction.module.css` is the app's other retry box and is deliberately
 * NOT worn here: it paints `--bg-card` because every one of its consumers stands on a card, and
 * this one stands on the sheet's `--bg` canvas. */
.retry {
  margin-top: var(--space-3);
}

/* The quiet note beside „Własny kolor" — the mockup's `.blk>h3 small`, pushed to the far end. */
.blockNote {
  margin-left: auto;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* ── The six ready-made colours ───────────────────────────────────────────────────────────── */

.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
}

.tile {
  display: flex;
  gap: var(--space-2-5px);
  align-items: center;
  height: var(--sz-account-tile);
  padding: var(--space-0) var(--space-3) var(--space-0) var(--space-2);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-align: left;
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-account-tile);
}

.tile:hover {
  color: var(--text);
  border-color: var(--border-2);
}

.tile:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

/* The chosen one. An INK ring rather than an accent one: the tile already carries a colour, and a
 * second colour marking it would be two hues arguing about which is the state. `--ring-field` is
 * the hairline every focused field in this app already wears — the shadow doubles the border so the
 * ring reads at two pixels without the box growing by one. */
.tileOn {
  color: var(--text);
  background: var(--bg-card);
  border-color: var(--text);
  box-shadow: var(--ring-field);
}

/* The swatch. Its fill is DATA — the preset's gradient, handed over inline by the component — so
 * the only thing declared here is the box and the hairline that keeps a pale colour off the tile. */
.swatch {
  flex: none;
  width: var(--sz-account-swatch);
  height: var(--sz-account-swatch);
  border: var(--border-hairline) solid var(--swatch-edge);
  border-radius: var(--radius-account-swatch);
}

/* ── The custom colour ─────────────────────────────────────────────────────────────────────── */

.custom {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-5);
}

/* The column beside the picker: the field at the top, the chip under it, and the save button
 * pushed to the bottom corner by the gap that grows. */
.side {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: flex-start;
  min-width: 0;
}

.hexField {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  align-self: stretch;
  height: var(--h-field);
  padding: var(--space-0) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-account-tile);
}

.hexField:focus-within {
  border-color: var(--text);
}

.hexHash {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.hexInput {
  flex: 1;
  min-width: 0;
  font-family: var(--font-body);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-account-hex);
  color: var(--text);
  text-transform: uppercase;
  background: transparent;
  border: 0;
  outline: none;
}

.aa {
  gap: var(--space-1-5px);
}

/* What the picker cannot show on its own: the colour actually painted, or why nothing saves. */
.adjusted {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.aaIcon {
  display: block;
  flex: none;
  width: var(--sz-account-glyph-sm);
  height: var(--sz-account-glyph-sm);
}

/* The save button sits in the bottom-right corner of its column — `margin-top: auto` takes the
 * slack, `align-self` takes the side. */
.save {
  gap: var(--space-1-5px);
  height: var(--h-account-primary);
  padding: var(--space-0) var(--space-14px) var(--space-0) var(--space-10px);
  margin-top: auto;
  margin-left: auto;
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  letter-spacing: normal;
  border-radius: var(--radius-12px);
}

.saveIcon {
  display: block;
  flex: none;
  width: var(--sz-account-primary-glyph);
  height: var(--sz-account-primary-glyph);
}
